import { forwardRef } from 'react';
import { Stack as MuiStack, StackProps as MuiStackProps } from '@mui/material';

/**
 * Stack component - Layout primitive for vertical/horizontal layouts with automatic spacing.
 *
 * This is a thin wrapper around Material-UI's Stack component that provides:
 * - Direction control (row/column/row-reverse/column-reverse)
 * - Automatic spacing between children using theme spacing units
 * - Optional dividers between children
 * - Responsive direction and spacing
 * - Theme-aware styling via sx prop
 *
 * Based on analysis of mobile app usage (2 instances):
 * - Mobile app heavily prefers Box with flexbox over Stack
 * - Common patterns: Horizontal button groups, simple wrappers
 * - Responsive spacing is used for different screen sizes
 * - No divider usage found in current mobile implementation
 *
 * @example
 * // Horizontal button group with spacing (ModalHeader pattern)
 * <Stack direction="row" spacing={{ xs: 0.5, sm: 1.5 }} alignItems="center">
 *   <IconButton>...</IconButton>
 *   <IconButton>...</IconButton>
 * </Stack>
 *
 * @example
 * // Vertical layout with spacing
 * <Stack spacing={2}>
 *   <TextField label="Field 1" />
 *   <TextField label="Field 2" />
 *   <Button>Submit</Button>
 * </Stack>
 *
 * @example
 * // With dividers
 * <Stack direction="row" spacing={2} divider={<Divider orientation="vertical" flexItem />}>
 *   <Item />
 *   <Item />
 *   <Item />
 * </Stack>
 *
 * @example
 * // Responsive direction (column on mobile, row on desktop)
 * <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
 *   <Box flex={1}>Left</Box>
 *   <Box flex={1}>Right</Box>
 * </Stack>
 *
 * @example
 * // Full-width wrapper (LoginLayout pattern)
 * <Stack sx={{ width: 1 }}>
 *   {children}
 * </Stack>
 */
export type StackProps = MuiStackProps;

/**
 * Stack component - Vertical/horizontal layout with automatic spacing.
 *
 * A flexible container for stacking elements with consistent spacing between children.
 * This is a thin wrapper around MUI Stack that preserves all Material-UI capabilities
 * while providing a consistent import path through the ui-library.
 */
export const Stack = forwardRef<HTMLDivElement, StackProps>((props, ref) => (
  <MuiStack ref={ref} {...props} />
));

Stack.displayName = 'Stack';
